import { Link } from "react-router-dom";
import { useState } from "react";
import { Wallet, TrendingUp, Sparkles, ShieldCheck, Zap, Clock, ArrowRight, CheckCircle2, Users } from "lucide-react";
import { useLang } from "../context/LangContext";

const EARN_HERO_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&h=800&fit=crop&auto=format&q=70";

export default function Earn() {
  const { t } = useLang();
  const [hoursPerWeek, setHoursPerWeek] = useState(20);
  const [avgHourly, setAvgHourly] = useState(300);

  const perWeek = hoursPerWeek * avgHourly;
  const monthly = Math.round(perWeek * 4.3);
  const partnerShare = Math.round(monthly * 0.67);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-24">
      {/* HERO */}
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 fade-up">
          <div className="chip" style={{ borderColor: "var(--secondary)", color: "var(--secondary)" }}>
            <Wallet className="w-3.5 h-3.5" /> Earnings Platform
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-7xl font-bold leading-[0.98]" style={{ color: "var(--text-main)" }}>
            Earn <span className="italic" style={{ color: "var(--secondary)" }}>₹25,000+</span>
            <br />/ month, extra.
          </h1>
          <p className="mt-6 text-lg leading-relaxed max-w-xl" style={{ color: "var(--text-muted)" }}>
            Share help. Earn real money. Verified Mates in Pune & Mumbai region earn <b>₹25K–₹60K/month</b> in their free hours — carrying luggage at the station, meeting flights, running exhibitions.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register?role=assistant" data-testid="earn-register-btn">
              <button className="btn-primary inline-flex items-center gap-2">
                {t("cta_earn")} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/become-mate" data-testid="earn-become-mate-btn">
              <button className="px-6 py-3 rounded-full border-2 border-[color:var(--primary)] text-[color:var(--primary)] bg-white font-bold hover:bg-[color:var(--primary)] hover:text-white transition-colors">
                Become a Mate →
              </button>
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            <span className="chip"><ShieldCheck className="w-3.5 h-3.5" /> KYC-verified helpers</span>
            <span className="chip"><Zap className="w-3.5 h-3.5" /> Paid within 24 hrs</span>
            <span className="chip"><Clock className="w-3.5 h-3.5" /> Choose your hours</span>
          </div>
        </div>
        <div className="lg:col-span-6">
          <img src={EARN_HERO_IMG} alt="Earn as a HeylpMate" className="w-full h-[400px] md:h-[500px] object-cover rounded-3xl border border-black/10 shadow-[0_20px_60px_-20px_rgba(29,46,95,0.35)]" />
        </div>
      </div>

      {/* CALCULATOR */}
      <section className="mt-20 rounded-3xl border-2 border-black/10 bg-white p-6 md:p-10">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="label-eyebrow">Estimate your earnings</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight">
              Your <span className="italic" style={{ color: "var(--secondary)" }}>real</span> monthly potential.
            </h2>
            <p className="mt-3 text-sm text-neutral-600">Drag the sliders to see what you can take home in your free hours.</p>
            <div className="mt-6 space-y-5">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Hours per week</span>
                  <span className="font-bold" style={{ color: "var(--secondary)" }}>{hoursPerWeek} hrs</span>
                </div>
                <input
                  data-testid="earn-hours-slider"
                  type="range" min="5" max="60" step="5"
                  value={hoursPerWeek}
                  onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                  className="w-full mt-2 accent-[color:var(--secondary)]"
                />
              </div>
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Avg. ₹ per hour</span>
                  <span className="font-bold" style={{ color: "var(--secondary)" }}>₹{avgHourly}</span>
                </div>
                <input
                  data-testid="earn-rate-slider"
                  type="range" min="150" max="600" step="50"
                  value={avgHourly}
                  onChange={(e) => setAvgHourly(Number(e.target.value))}
                  className="w-full mt-2 accent-[color:var(--secondary)]"
                />
              </div>
            </div>
          </div>
          <div className="rounded-3xl p-8 text-white" style={{ background: "var(--primary)" }}>
            <div className="text-xs uppercase tracking-widest text-white/70">Estimated monthly earnings</div>
            <div className="font-display text-6xl mt-3">₹{monthly.toLocaleString("en-IN")}</div>
            <div className="mt-1 text-white/70 text-sm">Gross · before platform fee</div>
            <div className="border-t border-white/20 my-5" />
            <div className="flex justify-between text-sm">
              <span className="text-white/70">You take home (67%)</span>
              <span className="font-bold" style={{ color: "var(--secondary)" }}>₹{partnerShare.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-white/70">Platform commission</span>
              <span>₹{(monthly - partnerShare).toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY EARN */}
      <section className="mt-20">
        <span className="label-eyebrow">why HeylpMate pays more</span>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight max-w-2xl">
          Real work. <span className="italic" style={{ color: "var(--secondary)" }}>Real payouts.</span> No middleman.
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {[
            { i: TrendingUp, t: "You keep 67%", d: "Transparent split — customer pays ₹299, you take ₹200. Every booking, every time." },
            { i: Sparkles, t: "Featured badge", d: "Upgrade to Featured Mate (₹199/mo) — appear top-of-search in your city with a verified crown." },
            { i: Users, t: "Multi-service", d: "One profile, multiple Assists — railway, airport, hospital, corporate. Earn from every category you're qualified for." },
          ].map(({ i: I, t: ttl, d }) => (
            <div key={ttl} className="rounded-3xl border border-black/10 bg-white p-6 card-lift">
              <div className="w-11 h-11 rounded-full grid place-items-center" style={{ background: "var(--secondary)" }}>
                <I className="w-5 h-5 text-white" />
              </div>
              <div className="font-display text-2xl mt-5" style={{ color: "var(--primary)" }}>{ttl}</div>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mt-20">
        <span className="label-eyebrow">3 steps to your first payout</span>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {[
            ["Register free", "Sign up as I Want to Help — takes under 60 seconds."],
            ["Verify KYC", "Upload one govt ID (Aadhaar, PAN, DL). HeylpMate calls to verify."],
            ["Start earning", "Accept jobs in your city, get paid within 24 hours to your UPI/bank."],
          ].map(([ttl, d], i) => (
            <div key={ttl} className="rounded-3xl border border-black/10 bg-white p-6">
              <div className="font-display text-3xl" style={{ color: "var(--secondary)" }}>0{i + 1}</div>
              <div className="font-display text-xl mt-3" style={{ color: "var(--primary)" }}>{ttl}</div>
              <p className="text-sm text-neutral-600 mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-20 rounded-3xl overflow-hidden relative text-white p-10 md:p-14" style={{ background: "var(--secondary)" }}>
        <div className="grain absolute inset-0 opacity-25" />
        <div className="relative grid md:grid-cols-2 gap-8 items-center">
          <div>
            <CheckCircle2 className="w-8 h-8 mb-4" />
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Sign up today. Get your first job this week.
            </h2>
            <p className="mt-4 text-white/90 max-w-md">
              Registration is free. Featured Mate upgrade optional. Payouts within 24 hrs to your UPI.
            </p>
          </div>
          <div>
            <Link to="/register?role=assistant" data-testid="earn-cta-register">
              <button className="w-full py-4 rounded-full bg-white text-[color:var(--secondary)] font-bold text-lg hover:bg-neutral-100 transition-colors">
                Register free · Earn ₹25K+ →
              </button>
            </Link>
            <p className="text-[11px] text-white/80 mt-3 text-center">
              *₹25K based on 20 hrs/week at avg ₹300/hr, minus platform commission
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
